<!--
  Distributor App sub-task 11: Tax-nexus single-table UI.

  Card: Distributor App (https://trello.com/c/QV2U4KhF)

  Owner-facing editor for the jurisdictions where the distributor has
  sales-tax nexus. Replaces the DistributorComingSoon frame at
  /distributor/account/compliance/tax-nexus.

  UX (Jake, 2026-06-02; layout revised 2026-10-19): one checkbox per
  jurisdiction (50 states + DC), full state names, in a <table> with a
  SECTION per Census region. Each section has a header row carrying the
  "select all in region" control and a responsive multi-column grid of
  that region's states (the first cut was one state per row, which read as
  one long list). Still "a table of checkboxes" - NOT a two-list /
  shuttle / chip layout.

  Persistence:
    - Load:  GET  /distributor/getDistributorNexusStates/<id>
    - Save:  POST /distributor/setDistributorNexusStates/<id>
             (gated on the Owner-only `distributor.compliance.manage`
             capability - the route guard mirrors this).

  Empty set = collect nowhere (Bug Intake #565, Mike's rule): when the stored
  set is empty/null (a fresh distributor that has never set nexus), NOTHING
  is pre-checked and a prompt asks the Owner to tick their home state first.
  The backend sales-tax calculation collects nothing for an empty set, so the
  page shows what checkout does; pre-ticking every state meant one Save or
  Confirm made the account collect (and owe remittance) in all 51
  jurisdictions. A failed load also leaves nothing ticked, so a Save after an
  error can never widen nexus. An existing partial selection is reflected
  as-is and never silently overwritten.

  The full state-name strings are the canonical values from
  `@/data/usStates` (which mirror api/src/helper_tables/us_state_codes.csv)
  so the persisted list matches what the backend sales-tax calculation
  compares against.
-->
<template>
    <section class="tax-nexus" data-testid="tax-nexus-editor">
        <header class="tax-nexus__head">
            <h1 class="tax-nexus__title">Sales Tax Nexus</h1>
            <p class="tax-nexus__intro">
                Select the US jurisdictions where your business has
                sales-tax nexus. Sales tax is collected on orders shipped
                to checked jurisdictions only. Start with the state your
                business is located in, and add other states as you gain
                nexus there.
            </p>
        </header>

        <p
            v-if="loaded && !loadError && selectedNames.length === 0"
            class="tax-nexus__prompt"
            data-testid="tax-nexus-empty-prompt"
            role="status"
        >
            No states are selected, so sales tax is not collected anywhere
            yet. Tick the state where your business is located, then save.
        </p>

        <p
            v-if="loadError"
            class="tax-nexus__error"
            data-testid="tax-nexus-load-error"
        >
            We could not load your current nexus settings. Please reload
            the page and try again.
        </p>

        <!--
          The shared region-grouped, multi-column grid (NexusStatesGrid): one
          block per Census region with a "select all in region" box and that
          region's states in columns, so 51 jurisdictions fit in a few
          screens. Same control the SuperAdmin distributor pages use.
        -->
        <div data-testid="tax-nexus-table">
            <NexusStatesGrid
                :model-value="selectedNames"
                legend="Jurisdictions"
                @update:model-value="onSelectionChange"
            />
        </div>

        <!--
          Bug Intake #502: signup adds your company's state for you, so the
          "Sales tax states reviewed" setup step stays open until you confirm
          the list here. The button saves what is on screen, then confirms.
        -->
        <p
            v-if="confirmedAt"
            class="tax-nexus__confirmed"
            data-testid="tax-nexus-confirmed"
            role="status"
        >
            You confirmed these sales tax states on {{ confirmedOn }}. You can
            still add or remove states; they stay confirmed.
        </p>
        <p
            v-else
            class="tax-nexus__prompt"
            data-testid="tax-nexus-confirm-prompt"
        >
            Review the list above, then confirm it. Stores cannot go live
            until you do.
        </p>

        <footer class="tax-nexus__foot">
            <button
                type="button"
                class="tax-nexus__save"
                data-testid="tax-nexus-save"
                :disabled="saving || !canWrite"
                @click="onSave"
            >
                {{ saving ? "Saving..." : "Save nexus settings" }}
            </button>
            <button
                v-if="!confirmedAt"
                type="button"
                class="tax-nexus__save"
                data-testid="tax-nexus-confirm"
                :disabled="saving || !canWrite || selectedNames.length === 0"
                @click="onConfirm"
            >
                {{ saving ? "Confirming..." : "Confirm sales tax states" }}
            </button>
            <span
                v-if="saved"
                class="tax-nexus__success"
                data-testid="tax-nexus-save-success"
                role="status"
            >
                Nexus settings saved.
            </span>
            <span
                v-if="saveError"
                class="tax-nexus__error"
                data-testid="tax-nexus-save-error"
                role="alert"
            >
                Save failed. Please try again.
            </span>
        </footer>
    </section>
</template>

<script>
import { US_JURISDICTIONS } from "@/data/usStates";
import NexusStatesGrid from "@/pages/distributor/account/compliance/NexusStatesGrid.vue";
import {
    confirmNexusStates,
    getDistributorNexusStates,
    getNexusConfirmation,
    setDistributorNexusStates,
} from "@/services/distributorService";
import { useAccountStore } from "@/stores/account";
import { notifyTaxNexusChanged } from "@/stores/sidebarRefresh";

export default {
    name: "TaxNexusEditor",
    components: { NexusStatesGrid },
    props: {
        // Optional explicit distributor id. When absent, resolved from the
        // authenticated account store. The prop exists so the component is
        // mount-testable without a live Pinia store.
        distributorId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            jurisdictions: US_JURISDICTIONS,
            // name -> boolean. The full state name is the key so the
            // persisted list is built directly from the canonical names.
            selected: {},
            saving: false,
            saved: false,
            saveError: false,
            loadError: false,
            // True once the first load attempt has settled, so the empty
            // prompt does not flash while the stored set is still loading.
            loaded: false,
            // ISO timestamp of the Owner's confirmation, or null.
            confirmedAt: null,
        };
    },
    computed: {
        resolvedDistributorId() {
            if (this.distributorId !== null && this.distributorId !== "") {
                return this.distributorId;
            }
            // Resolve lazily from the account store so unit tests that pass
            // the prop never touch Pinia.
            try {
                const account = useAccountStore();
                return account.distributor ? account.distributor.id : null;
            } catch (e) {
                return null;
            }
        },
        // Writes are allowed only once the stored set loaded cleanly: after a
        // failed load nothing is ticked, so a Save would post [] and wipe the
        // stored states (tax collection off everywhere).
        canWrite() {
            return this.loaded && !this.loadError;
        },
        confirmedOn() {
            const d = new Date(this.confirmedAt);
            return Number.isNaN(d.getTime())
                ? this.confirmedAt
                : d.toLocaleDateString();
        },
        selectedNames() {
            return this.jurisdictions
                .map((j) => j.name)
                .filter((name) => this.selected[name] === true);
        },
    },
    created() {
        this.loadNexusStates();
        this.loadConfirmation();
    },
    methods: {
        normalizeStoredStates(raw) {
            // The GET endpoint returns ONE row per distributor, and
            // `nexus_states` is itself a JSON list -- so a single distributor
            // reads back as a list-of-one-list: `[["California", "Texas"]]`.
            // (The legacy DistributorAccountInfo.vue consumer unwraps the
            // same way via `result.data[0]`.) Edge cases the endpoint emits:
            //   - existing nexus:  [["California", ...]]  -> inner list
            //   - empty nexus:     [[]]                   -> []
            //   - null column:     [null]                 -> []
            //   - no matching row: "" (empty-string body) -> []
            // Feeding the raw outer list straight into `new Set(...)` would
            // build a Set whose only member is the inner ARRAY, so every
            // `has(stateName)` is false -- the data-loss bug this guards.
            if (!Array.isArray(raw) || raw.length === 0) {
                return [];
            }
            const inner = raw[0];
            return Array.isArray(inner) ? inner.filter((s) => s != null) : [];
        },
        applySelection(rawStored) {
            const next = {};
            const stored = this.normalizeStoredStates(rawStored);
            // An empty / null stored set selects nothing (collect nowhere).
            const storedSet = new Set(stored);
            for (const j of this.jurisdictions) {
                next[j.name] = storedSet.has(j.name);
            }
            this.selected = next;
            this.loaded = true;
        },
        async loadNexusStates() {
            const id = this.resolvedDistributorId;
            if (id === null) {
                // No distributor in context -> nothing selected.
                this.applySelection([]);
                return;
            }
            try {
                const response = await getDistributorNexusStates(id);
                this.applySelection(response ? response.data : []);
            } catch (e) {
                this.loadError = true;
                this.applySelection([]);
            }
        },
        async loadConfirmation() {
            const id = this.resolvedDistributorId;
            if (id === null) return;
            try {
                const response = await getNexusConfirmation(id);
                this.confirmedAt = response?.data?.confirmed_at || null;
            } catch (e) {
                // Unknown: leave the confirm button offered; confirming is
                // idempotent on the server.
                this.confirmedAt = null;
            }
        },
        onSelectionChange(names) {
            // Rebuild the name -> boolean map from the grid's emitted array.
            const chosen = new Set(names);
            const next = {};
            for (const j of this.jurisdictions) {
                next[j.name] = chosen.has(j.name);
            }
            this.selected = next;
        },
        async onConfirm() {
            if (!this.canWrite) return;
            this.saving = true;
            this.saved = false;
            this.saveError = false;
            const id = this.resolvedDistributorId;
            try {
                // Confirm what the Owner is looking at, not a stale stored
                // list: save the selection first, and confirm only if it took.
                await setDistributorNexusStates(id, this.selectedNames);
                const response = await confirmNexusStates(id);
                this.confirmedAt = response?.data?.confirmed_at || null;
                this.saved = true;
                this.$emit("saved", this.selectedNames);
                notifyTaxNexusChanged();
            } catch (e) {
                this.saveError = true;
            } finally {
                this.saving = false;
            }
        },
        async onSave() {
            if (!this.canWrite) return;
            this.saving = true;
            this.saved = false;
            this.saveError = false;
            const id = this.resolvedDistributorId;
            try {
                await setDistributorNexusStates(id, this.selectedNames);
                this.saved = true;
                this.$emit("saved", this.selectedNames);
                // The nexus-state count changed -> refresh the sidebar's
                // tax_nexus counter ("N states"). Success path only; a failed
                // save leaves the persisted count unchanged. Seam docs +
                // https://trello.com/c/g9K52FsA.
                notifyTaxNexusChanged();
            } catch (e) {
                this.saveError = true;
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.tax-nexus {
    max-width: 100%;
    margin: 0 auto;
    padding: 0 var(--tf-sp-4, 16px);
    color: var(--tf-fg-2, var(--tf-fg-2));
}
.tax-nexus__title {
    font-size: var(--tf-size-h2, 1.5rem);
    font-weight: 600;
    color: var(--tf-fg-1, var(--tf-fg-1));
    margin-bottom: var(--tf-sp-3, 12px);
}
.tax-nexus__intro {
    line-height: 1.5;
    margin-bottom: var(--tf-sp-6, 24px);
}
.tax-nexus__foot {
    display: flex;
    align-items: center;
    gap: var(--tf-sp-4, 16px);
    margin-top: var(--tf-sp-6, 24px);
}
.tax-nexus__save {
    background: var(--tf-primary, var(--tf-primary));
    color: var(--tf-fg-on-primary, var(--tf-bg));
    border: none;
    border-radius: var(--tf-radius-sm, 8px);
    padding: var(--tf-sp-2, 8px) var(--tf-sp-5, 20px);
    font-size: var(--tf-size-body, 1rem);
    cursor: pointer;
}
.tax-nexus__save:disabled {
    opacity: 0.6;
    cursor: default;
}
.tax-nexus__success {
    color: var(--tf-success-dark, var(--tf-success-dark));
    font-weight: 600;
}
.tax-nexus__prompt,
.tax-nexus__confirmed {
    margin-top: var(--tf-sp-4, 16px);
    line-height: 1.5;
}
.tax-nexus__confirmed {
    color: var(--tf-success-dark, var(--tf-success-dark));
    font-weight: 600;
}
.tax-nexus__error {
    color: var(--tf-error, var(--tf-red));
    font-weight: 600;
}
</style>
